import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import TablePagination from '@mui/material/TablePagination';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import {
  Check,
  ExternalLink,
  File,
  FileText,
  Image as ImageIcon,
  Film,
  Eye,
  Pencil,
  RefreshCcw,
  Trash2,
  Upload,
  User,
  Phone,
  Mail,
  X,
  FileText as FileTextIcon
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const StyledCard = styled(Card)({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});

const formatDateOnly = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString('it-IT');
  } catch {
    return value;
  }
};

const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = size;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const digits = value >= 100 ? 0 : value >= 10 ? 1 : 1;
  return `${value.toFixed(digits)} ${units[unitIndex]}`;
};

const getFileTypeLabel = (row) => {
  const fromFileName = String(row?.file_name || '')
    .split('.')
    .pop()
    ?.trim()
    .toUpperCase();
  if (fromFileName && fromFileName !== String(row?.file_name || '').toUpperCase()) {
    return fromFileName.slice(0, 6);
  }
  const mime = String(row?.file_mime || '').toLowerCase();
  if (mime.includes('pdf')) return 'PDF';
  if (mime.includes('jpeg') || mime.includes('jpg')) return 'JPG';
  if (mime.includes('png')) return 'PNG';
  if (mime.includes('webp')) return 'WEBP';
  return 'FILE';
};

const getFileTypeIcon = (row) => {
  const type = getFileTypeLabel(row);
  if (type === 'PDF') return FileText;
  if (type === 'JPG' || type === 'PNG' || type === 'WEBP') return ImageIcon;
  if (type === 'MOV') return Film;
  return File;
};

const nowDateTimeLocal = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const toDateTimeLocalInput = (value) => {
  if (!value) return nowDateTimeLocal();
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return nowDateTimeLocal();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const renderContactPreview = ({ name, phone, email, fallback = '-' }) => (
  <Box sx={{ minWidth: 220, display: 'grid', gap: 0.25 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <User size={14} color="#2563eb" />
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {name || fallback}
      </Typography>
    </Box>
    {phone && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Phone size={13} color="#64748b" />
        <Typography variant="caption" color="text.secondary">
          {phone}
        </Typography>
      </Box>
    )}
    {email && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Mail size={13} color="#64748b" />
        <Typography variant="caption" color="text.secondary">
          {email}
        </Typography>
      </Box>
    )}
  </Box>
);

const getFirstDayOfMonth = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${yyyy}-${mm}-01`;
};

const getTodayDate = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export default function OpenBankingContabili() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [totalItems, setTotalItems] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('data_caricamento');
  
  const [filters, setFilters] = useState({
    search: '',
    from_date: getFirstDayOfMonth(),
    to_date: getTodayDate(),
    uploaded_by_user_id: ''
  });

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadCustomerId, setUploadCustomerId] = useState('');
  const [customerLoading, setCustomerLoading] = useState(false);
  const [uploadCustomerSearch, setUploadCustomerSearch] = useState('');
  const [uploadCustomerOptions, setUploadCustomerOptions] = useState([]);
  const [uploadDataCaricamento, setUploadDataCaricamento] = useState(nowDateTimeLocal());
  const [uploadNote, setUploadNote] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingNote, setEditingNote] = useState('');
  const [editingDataCaricamento, setEditingDataCaricamento] = useState(nowDateTimeLocal());
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [openInNewTabLoading, setOpenInNewTabLoading] = useState(false);
  const [uploaderLoading, setUploaderLoading] = useState(false);
  const [uploaderOptions, setUploaderOptions] = useState([]);
  const [uploaderSearchInput, setUploaderSearchInput] = useState('');

  const selectedCustomer = useMemo(
    () => uploadCustomerOptions.find((item) => Number(item.id) === Number(uploadCustomerId)) || null,
    [uploadCustomerOptions, uploadCustomerId]
  );
  const selectedUploader = useMemo(
    () =>
      uploaderOptions.find((item) => Number(item.id) === Number(filters.uploaded_by_user_id)) || null,
    [uploaderOptions, filters.uploaded_by_user_id]
  );

  const formatCustomerLabel = (item) => {
    if (!item) return '';
    const fullName = [item.name, item.surname].filter(Boolean).join(' ').trim();
    return fullName || item.email || item.telephone || `ID ${item.id}`;
  };

  const fetchData = async (options = {}) => {
    setLoading(true);
    try {
      const effectivePage = typeof options.nextPage === 'number' ? options.nextPage : page;
      const effectiveLimit =
        typeof options.nextRowsPerPage === 'number' ? options.nextRowsPerPage : rowsPerPage;

      const params = {
        page: effectivePage + 1,
        limit: effectiveLimit,
        include_customer: 1,
        orderBy: orderBy,
        orderDirection: order
      };

      const searchTerm = filters.search.trim();
      if (searchTerm) {
        params.search = searchTerm;
        try {
          const customerRes = await api.get('/customer', {
            params: {
              context: 'pratiche',
              page: 1,
              limit: 25,
              search: searchTerm
            },
            timeout: 5000
          });
          const customerList = Array.isArray(customerRes.data?.data) ? customerRes.data.data : [];
          const matchedCustomerIds = customerList
            .map((item) => Number(item?.id))
            .filter((id) => Number.isFinite(id) && id > 0);
          if (matchedCustomerIds.length > 0) {
            params.customer_ids = matchedCustomerIds.join(',');
          }
        } catch (customerLookupError) {
          // Non bloccare la ricerca contabili se lookup cliente non disponibile.
          console.error('Customer lookup failed for contabili search:', customerLookupError);
        }
      }
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      if (filters.uploaded_by_user_id) params.uploaded_by_user_id = filters.uploaded_by_user_id;

      const res = await api.get('/open-banking/contabili', { params });
      const payload = res.data || {};
      setRows(Array.isArray(payload.data) ? payload.data : []);
      setTotalItems(Number(payload.total || 0));
    } catch (error) {
      console.error('Error fetching contabili:', error);
      const backendMessage =
        (typeof error?.response?.data === 'string' && error.response.data) ||
        error?.response?.data?.message ||
        error?.message;
      toast.error(
        backendMessage || 'Errore nel caricamento delle contabili'
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchUploaderOptions = async (searchText = '') => {
    try {
      setUploaderLoading(true);
      const res = await api.get('/open-banking/contabili/uploaders', {
        params: {
          search: searchText || ''
        }
      });
      const items = Array.isArray(res.data?.data) ? res.data.data : [];
      setUploaderOptions(items);
    } catch (error) {
      console.error('Error fetching uploader options:', error);
    } finally {
      setUploaderLoading(false);
    }
  };

  const fetchCustomers = async (searchText = '') => {
    try {
      setCustomerLoading(true);
      const res = await api.get('/customer', {
        params: {
          context: 'pratiche',
          page: 1,
          limit: 25,
          search: searchText || ''
        }
      });
      const items = Array.isArray(res.data?.data) ? res.data.data : [];
      setUploadCustomerOptions(items);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setCustomerLoading(false);
    }
  };

  useEffect(() => {
    fetchData({ nextPage: 0 });
    fetchUploaderOptions('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchData({ nextPage: 0 });
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, orderBy, order]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchUploaderOptions(uploaderSearchInput);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [uploaderSearchInput]);

  useEffect(() => {
    if (!uploadDialogOpen) return;
    const handler = setTimeout(() => {
      fetchCustomers(uploadCustomerSearch);
    }, 300);
    return () => clearTimeout(handler);
  }, [uploadDialogOpen, uploadCustomerSearch]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
    fetchData({ nextPage: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
    fetchData({ nextPage: 0, nextRowsPerPage: value });
  };

  const handleOpenUploadDialog = () => {
    setUploadDialogOpen(true);
    setUploadCustomerId('');
    setUploadCustomerSearch('');
    setUploadDataCaricamento(nowDateTimeLocal());
    setUploadNote('');
    setUploadFile(null);
    fetchCustomers('');
  };

  const handleCloseUploadDialog = () => {
    if (uploading) return;
    setUploadDialogOpen(false);
  };

  const handleUpload = async () => {
    if (!uploadCustomerId) {
      toast.error('Seleziona un cliente');
      return;
    }
    if (!uploadFile) {
      toast.error('Seleziona il file contabile');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('customer_id', String(uploadCustomerId));
      formData.append('data_caricamento', uploadDataCaricamento);
      formData.append('note', uploadNote || '');
      formData.append('file', uploadFile);

      await api.post('/open-banking/contabili', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Contabile caricata correttamente');
      setUploadDialogOpen(false);
      setPage(0);
      fetchData({ nextPage: 0 });
    } catch (error) {
      console.error('Error uploading contabile:', error);
      toast.error(error?.response?.data?.message || 'Errore nel caricamento della contabile');
    } finally {
      setUploading(false);
    }
  };

  const revokePreviewUrl = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
  };

  const handleOpenPreview = async (row) => {
    setPreviewRow(row);
    setPreviewOpen(true);
    revokePreviewUrl();
    const mime = String(row?.file_mime || '').toLowerCase();
    const isPreviewable = mime.includes('pdf') || mime.startsWith('image/');
    if (!isPreviewable) return;

    try {
      setPreviewLoading(true);
      const res = await api.get(`/open-banking/contabili/${row.id}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: row.file_mime || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Error loading contabile preview:', error);
      toast.error("Errore durante il caricamento dell'anteprima");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewRow(null);
    revokePreviewUrl();
  };

  const handleOpenPreviewInNewTab = async () => {
    if (!previewRow) return;
    if (previewUrl) {
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      setOpenInNewTabLoading(true);
      const res = await api.get(`/open-banking/contabili/${previewRow.id}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: previewRow.file_mime || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening contabile in new tab:', error);
      toast.error("Errore durante l'apertura del documento");
    } finally {
      setOpenInNewTabLoading(false);
    }
  };

  const handleOpenDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) {
      handleCloseDeleteDialog();
      return;
    }
    try {
      setDeleting(true);
      await api.delete(`/open-banking/contabili/${deleteTarget.id}`);
      toast.success('Contabile eliminata');
      handleCloseDeleteDialog();
      fetchData({ nextPage: page });
    } catch (error) {
      console.error('Error deleting contabile:', error);
      toast.error(error?.response?.data?.message || "Errore durante l'eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  const startEdit = (row) => {
    setEditingRowId(row.id);
    setEditingNote(row.note || '');
    setEditingDataCaricamento(toDateTimeLocalInput(row.data_caricamento));
  };

  const cancelEdit = () => {
    if (savingEdit) return;
    setEditingRowId(null);
    setEditingNote('');
    setEditingDataCaricamento(nowDateTimeLocal());
  };

  const saveEdit = async (row) => {
    try {
      setSavingEdit(true);
      const res = await api.patch(`/open-banking/contabili/${row.id}`, {
        note: editingNote || '',
        data_caricamento: editingDataCaricamento
      });

      const updatedItem = res.data?.item;
      if (updatedItem) {
        setRows((prev) =>
          prev.map((item) => (item.id === row.id ? { ...item, ...updatedItem } : item))
        );
      } else {
        fetchData({ nextPage: page });
      }

      toast.success('Contabile aggiornata');
      cancelEdit();
    } catch (error) {
      console.error('Error updating contabile inline:', error);
      toast.error(error?.response?.data?.message || "Errore durante l'aggiornamento");
    } finally {
      setSavingEdit(false);
    }
  };

  const isRowDirty = (row) => {
    const originalNote = row.note || '';
    const originalData = toDateTimeLocalInput(row.data_caricamento);
    return editingNote !== originalNote || editingDataCaricamento !== originalData;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FileTextIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Contabili bonifici
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Archivio contabili ricevute e collegate ai clienti
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Upload size={16} />} onClick={handleOpenUploadDialog}>
            Carica contabile
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshCcw size={16} />}
            onClick={() => fetchData({ nextPage: page })}
          >
            Aggiorna
          </Button>
        </Box>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Ricerca (note, file, cliente)"
                value={filters.search}
                onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Data da"
                InputLabelProps={{ shrink: true }}
                value={filters.from_date}
                onChange={(event) => setFilters((prev) => ({ ...prev, from_date: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Data a"
                InputLabelProps={{ shrink: true }}
                value={filters.to_date}
                onChange={(event) => setFilters((prev) => ({ ...prev, to_date: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={uploaderOptions}
                loading={uploaderLoading}
                value={selectedUploader}
                inputValue={uploaderSearchInput}
                filterOptions={(options) => options}
                onInputChange={(_event, value) => setUploaderSearchInput(value)}
                onChange={(_event, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    uploaded_by_user_id: value?.id ? String(value.id) : ''
                  }))
                }
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                getOptionLabel={(option) => option?.name || option?.email || `Utente ${option?.id || ''}`}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ py: 1 }}>
                    <Box sx={{ minWidth: 240, display: 'grid', gap: 0.25 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {option.name || `Utente #${option.id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email || 'Email non disponibile'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.upload_count} caricamenti
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Caricato da"
                    placeholder="Seleziona utente..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {uploaderLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Totale:
            </Typography>
            <Chip size="small" label={`${totalItems.toLocaleString('it-IT')} contabili`} />
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'file_name'}
                    direction={orderBy === 'file_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('file_name')}
                  >
                    Nome / Note
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'customer_id'}
                    direction={orderBy === 'customer_id' ? order : 'asc'}
                    onClick={() => handleRequestSort('customer_id')}
                  >
                    Cliente
                  </TableSortLabel>
                </TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'data_caricamento'}
                    direction={orderBy === 'data_caricamento' ? order : 'asc'}
                    onClick={() => handleRequestSort('data_caricamento')}
                  >
                    Data
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Dimensione</TableCell>
                <TableCell>Caricato da</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Nessuna contabile trovata</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        {(() => {
                          const Icon = getFileTypeIcon(row);
                          return (
                            <Box
                              sx={{
                                mt: 0.2,
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                bgcolor: '#f1f5f9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Icon size={15} color="#334155" />
                            </Box>
                          );
                        })()}
                            <Box>
                               <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                 {row.file_name}
                               </Typography>
                               {editingRowId === row.id ? (
                                 <TextField
                                   size="small"
                                   fullWidth
                                   multiline
                                   minRows={1}
                                   maxRows={3}
                                   placeholder="Aggiungi una nota..."
                                   value={editingNote}
                                   onChange={(event) => setEditingNote(event.target.value)}
                                   sx={{ mt: 1 }}
                                 />
                               ) : (
                                 row.note && (
                                   <Typography 
                                     variant="caption" 
                                     color="text.secondary"
                                     sx={{ 
                                       display: '-webkit-box',
                                       WebkitLineClamp: 2,
                                       WebkitBoxOrient: 'vertical',
                                       overflow: 'hidden',
                                       mt: 0.5
                                     }}
                                   >
                                     {row.note}
                                   </Typography>
                                 )
                               )}
                             </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {renderContactPreview({
                        name: row.customer?.name,
                        phone: row.customer?.telephone,
                        email: row.customer?.email,
                        fallback: 'Non disponibile'
                      })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getFileTypeLabel(row)}
                        size="small"
                        sx={{
                          bgcolor: '#f1f5f9',
                          color: '#0f172a',
                          fontWeight: 700,
                          borderRadius: '999px'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {editingRowId === row.id ? (
                        <TextField
                          size="small"
                          type="datetime-local"
                          value={editingDataCaricamento}
                          onChange={(event) => setEditingDataCaricamento(event.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      ) : (
                        formatDateOnly(row.data_caricamento)
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                      {formatFileSize(row.file_size)}
                    </TableCell>
                    <TableCell>
                      {([row.uploaded_by_name, row.uploaded_by_surname].filter(Boolean).join(' ').trim() ||
                        row.uploaded_by_email ||
                        `Utente #${row.uploaded_by_user_id || '-'}`)}
                    </TableCell>
                    <TableCell align="right">
                      {editingRowId === row.id ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          {isRowDirty(row) && (
                            <Typography variant="caption" sx={{ color: 'warning.main', mr: 0.5 }}>
                              Modifiche non salvate
                            </Typography>
                          )}
                          <Tooltip title="Salva" arrow>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => saveEdit(row)}
                              disabled={savingEdit || !isRowDirty(row)}
                            >
                              <Check size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Annulla" arrow>
                            <IconButton size="small" onClick={cancelEdit} disabled={savingEdit}>
                              <X size={18} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <>
                          <Tooltip title="Modifica inline" arrow>
                            <IconButton size="small" onClick={() => startEdit(row)}>
                              <Pencil size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Anteprima" arrow>
                            <IconButton size="small" onClick={() => handleOpenPreview(row)}>
                              <Eye size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Elimina" arrow>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDeleteDialog(row)}
                            >
                              <Trash2 size={18} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[30, 60, 90]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Stai per eliminare la contabile selezionata. Questa azione non è reversibile.
          </Alert>
          <Box sx={{ display: 'grid', gap: 0.75 }}>
            <Typography variant="body2">
              <strong>File:</strong> {deleteTarget?.file_name || '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Cliente:</strong> {deleteTarget?.customer?.name || 'Non disponibile'}
            </Typography>
            <Typography variant="body2">
              <strong>Data:</strong> {formatDateOnly(deleteTarget?.data_caricamento)}
            </Typography>
            <Typography variant="body2">
              <strong>Dimensione:</strong> {formatFileSize(deleteTarget?.file_size)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Annulla
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Carica contabile bonifico</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info">
                Formati consentiti: PDF, JPG, JPEG, PNG, WEBP. Dimensione massima: 10MB.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={uploadCustomerOptions}
                loading={customerLoading}
                value={selectedCustomer}
                inputValue={uploadCustomerSearch}
                filterOptions={(options) => options}
                onInputChange={(_event, value) => setUploadCustomerSearch(value)}
                onChange={(_event, value) => setUploadCustomerId(value?.id ? String(value.id) : '')}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                getOptionLabel={(option) => formatCustomerLabel(option)}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ py: 1 }}>
                    <Box sx={{ minWidth: 260, display: 'grid', gap: 0.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <User size={14} color="#2563eb" />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {formatCustomerLabel(option)}
                        </Typography>
                      </Box>
                      {option.telephone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Phone size={13} color="#64748b" />
                          <Typography variant="caption" color="text.secondary">
                            {option.telephone}
                          </Typography>
                        </Box>
                      )}
                      {option.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Mail size={13} color="#64748b" />
                          <Typography variant="caption" color="text.secondary">
                            {option.email}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Cliente *"
                    placeholder="Cerca per nome, cognome, email, telefono..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customerLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                label="Data caricamento"
                InputLabelProps={{ shrink: true }}
                value={uploadDataCaricamento}
                onChange={(event) => setUploadDataCaricamento(event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Note aggiuntive"
                value={uploadNote}
                onChange={(event) => setUploadNote(event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="file"
                inputProps={{ accept: '.pdf,.jpg,.jpeg,.png,.webp' }}
                onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
              />
              {uploadFile && (
                <Typography variant="caption" color="text.secondary">
                  File selezionato: {uploadFile.name}
                </Typography>
              )}
              {selectedCustomer && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Cliente selezionato:{' '}
                  {[selectedCustomer.name, selectedCustomer.surname].filter(Boolean).join(' ')}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog} disabled={uploading}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleUpload} disabled={uploading}>
            {uploading ? 'Caricamento...' : 'Carica'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={previewOpen}
        onClose={handleClosePreview}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 480 },
            maxWidth: '100vw',
            borderLeft: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.06), 0 24px 48px rgba(15, 23, 42, 0.24)'
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Anteprima documento
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ExternalLink size={14} />}
                onClick={handleOpenPreviewInNewTab}
                disabled={!previewRow || openInNewTabLoading}
              >
                {openInNewTabLoading ? 'Apertura...' : 'Apri in nuova scheda'}
              </Button>
              <Button size="small" variant="text" onClick={handleClosePreview}>
                Chiudi
              </Button>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ minHeight: 0, flex: 1, overflow: 'auto' }}>
              {previewLoading && (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!previewLoading && previewRow && (() => {
                const mime = String(previewRow.file_mime || '').toLowerCase();
                const isPdf = mime.includes('pdf');
                const isImage = mime.startsWith('image/');

                if (previewUrl && isPdf) {
                  return (
                    <Box sx={{ height: { xs: '55vh', sm: '68vh' }, maxHeight: '78vh' }}>
                      <iframe
                        title={previewRow.file_name || 'Anteprima contabile'}
                        src={previewUrl}
                        style={{ border: 0, width: '100%', height: '100%' }}
                      />
                    </Box>
                  );
                }

                if (previewUrl && isImage) {
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <img
                        src={previewUrl}
                        alt={previewRow.file_name || 'Contabile'}
                        style={{ maxWidth: '100%', maxHeight: '72vh', borderRadius: 8 }}
                      />
                    </Box>
                  );
                }

                return (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Anteprima non disponibile per questo tipo di file.
                    </Typography>
                  </Box>
                );
              })()}
            </Box>

            {previewRow && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Dettagli
                </Typography>
                <Typography variant="body2">Nome: {previewRow.file_name || '-'}</Typography>
                <Typography variant="body2">
                  Tipo: {getFileTypeLabel(previewRow)}
                </Typography>
                <Typography variant="body2">
                  Data: {formatDateOnly(previewRow.data_caricamento)}
                </Typography>
                <Typography variant="body2">
                  Dimensione: {formatFileSize(previewRow.file_size)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
